import type { Cost } from '../types';
import { GameState } from '../game/GameState';
import { Deck } from '../game/Deck';

export interface CostCheckResult {
  canAfford: boolean;
  needsMemory: boolean;
  needsSanity: boolean;
  sanityCost: number;
  missingResources: Array<{ resource: string; needed: number; have: number }>;
  missingTime: number | null;
}

export function checkCost(
  cost: Cost | undefined,
  gameState: GameState,
  deck: Deck
): CostCheckResult {
  if (!cost) {
    return {
      canAfford: true,
      needsMemory: false,
      needsSanity: false,
      sanityCost: 0,
      missingResources: [],
      missingTime: null
    };
  }
  
  const missingResources: Array<{ resource: string; needed: number; have: number }> = [];
  let totalResourceDeficit = 0;
  
  // Check resource costs
  if (cost.money !== undefined && cost.money > 0) {
    const have = gameState.getResource('money');
    if (have < cost.money) {
      const needed = cost.money - have;
      missingResources.push({ resource: 'money', needed: cost.money, have });
      totalResourceDeficit += needed;
    }
  }
  
  if (cost.food !== undefined && cost.food > 0) {
    const have = gameState.getResource('food');
    if (have < cost.food) {
      const needed = cost.food - have;
      missingResources.push({ resource: 'food', needed: cost.food, have });
      totalResourceDeficit += needed;
    }
  }
  
  if (cost.motivation !== undefined && cost.motivation > 0) {
    const have = gameState.getResource('motivation');
    if (have < cost.motivation) {
      const needed = cost.motivation - have;
      missingResources.push({ resource: 'motivation', needed: cost.motivation, have });
      totalResourceDeficit += needed;
    }
  }
  
  if (cost.memory !== undefined && cost.memory > 0) {
    const have = gameState.getResource('memory');
    if (have < cost.memory) {
      const needed = cost.memory - have;
      missingResources.push({ resource: 'memory', needed: cost.memory, have });
      totalResourceDeficit += needed;
    }
  }
  
  if (cost.connections !== undefined && cost.connections > 0) {
    const have = gameState.getResource('connections');
    if (have < cost.connections) {
      const needed = cost.connections - have;
      missingResources.push({ resource: 'connections', needed: cost.connections, have });
      totalResourceDeficit += needed;
    }
  }
  
  // Health and Sanity costs are always payable (can go negative, then immediately checked)
  // Don't check them in the cost validation - they're always affordable
  
  // Check time cost
  // NOTE: When checkCost is called from Modal, the current card has already been removed
  // from the deck by playCard(), so we use deck size directly (no -1)
  let missingTime: number | null = null;
  if (cost.time !== undefined && cost.time > 0) {
    const availableToDiscard = deck.getDeckSize();
    if (availableToDiscard < cost.time) {
      missingTime = cost.time - availableToDiscard;
    }
  }
  
  // Can afford if:
  // 1. No missing resources (must have enough - no Sanity fallback for regular resources)
  // 2. No missing time cost
  // Health and Sanity costs are always payable (not checked here)
  const canAfford = 
    totalResourceDeficit === 0 &&
    missingTime === null;
  
  return {
    canAfford,
    needsMemory: false, // This will be set by RequirementChecker if needed
    needsSanity: false, // No longer using Sanity as fallback for regular resources
    sanityCost: 0, // No longer using Sanity as fallback
    missingResources,
    missingTime
  };
}

